import React, { useEffect, useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { CTAButtonLight } from '../ui/CTAButton';
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 24
  },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.55,
      delay: i * 0.12,
      ease: 'easeOut'
    }
  })
};
interface BarItem {
  label: string;
  value: number;
  isAccent: boolean;
}
const bars: BarItem[] = [{
  label: 'Indeed / ZipRecruiter',
  value: 1200,
  isAccent: false
}, {
  label: 'Recruiting Agencies',
  value: 3500,
  isAccent: false
}, {
  label: 'Internal Recruiter',
  value: 2400,
  isAccent: false
}, {
  label: 'The Hire Advantage',
  value: 350,
  isAccent: true
}];
const MAX_VALUE = 4000;
function AnimatedBar({
  item,
  index,
  animate
}: {
  item: BarItem;
  index: number;
  animate: boolean;
}) {
  const [width, setWidth] = useState(0);
  const targetWidth = item.value / MAX_VALUE * 100;
  useEffect(() => {
    if (!animate) return;
    const timer = setTimeout(() => setWidth(targetWidth), index * 150 + 300);
    return () => clearTimeout(timer);
  }, [animate, targetWidth, index]);
  return <div className="flex items-center gap-4" data-id="element-198">
      <div className="w-44 sm:w-52 text-right flex-shrink-0" data-id="element-199">
        <span className={`text-sm font-medium ${item.isAccent ? 'text-[#1A2B4A] font-bold' : 'text-[#1A2B4A]/70'}`} data-id="element-200">
          {item.label}
        </span>
      </div>
      <div className="flex-1 flex items-center gap-3" data-id="element-201">
        <div className="flex-1 h-10 bg-[#1A2B4A]/8 rounded-full overflow-visible relative" data-id="element-202">
          <div className="h-full rounded-full transition-all duration-1000 ease-out" style={{
          width: `${width}%`,
          backgroundColor: item.isAccent ? '#4ECDC4' : '#E8655A',
          minWidth: width > 0 ? 8 : 0
        }} data-id="element-203" />
        </div>
        <span className={`text-sm font-bold flex-shrink-0 w-16 ${item.isAccent ? 'text-[#4ECDC4]' : 'text-[#E8655A]'}`} data-id="element-204">
          ${item.value.toLocaleString()}
        </span>
      </div>
    </div>;
}
// Mobile stat card
function MobileStatCard({
  value,
  label,
  sub,
  accent,
  custom,
  animate
}: {
  value: number;
  label: string;
  sub: string;
  accent: boolean;
  custom: number;
  animate: boolean;
}) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!animate) return;
    const delay = setTimeout(() => {
      let start: number | null = null;
      const step = (ts: number) => {
        if (!start) start = ts;
        const progress = Math.min((ts - start) / 1600, 1);
        const eased = 1 - Math.pow(1 - progress, 3);
        setCount(Math.floor(eased * value));
        if (progress < 1) requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    }, custom * 150 + 300);
    return () => clearTimeout(delay);
  }, [animate, value, custom]);
  return <motion.div custom={custom} variants={fadeUp} initial="hidden" animate={animate ? 'visible' : 'hidden'} className="rounded-2xl p-5 flex flex-col items-center text-center" style={{
    background: accent ? '#4ECDC4' : 'white',
    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
    minHeight: 120
  }} data-id="element-205">
      <p className="text-4xl font-black leading-none mb-1" style={{
      color: '#1A2B4A'
    }} data-id="element-206">
        {count}
      </p>
      <p className="text-xs font-black uppercase tracking-wider mb-1" style={{
      color: '#1A2B4A'
    }} data-id="element-207">
        {label}
      </p>
      <p className="text-xs leading-snug" style={{
      color: 'rgba(26,43,74,0.6)'
    }} data-id="element-208">
        {sub}
      </p>
    </motion.div>;
}
export function CostComparisonSection() {
  const ref = useRef(null);
  const inView = useInView(ref, {
    once: true,
    amount: 0.2
  });
  return <section className="w-full bg-[#F5F7FA] py-24 px-6" data-id="element-209">
      <div ref={ref} className="max-w-4xl mx-auto" data-id="element-210">
        <motion.div custom={0} variants={fadeUp} initial="hidden" animate={inView ? 'visible' : 'hidden'} className="text-center mb-14" data-id="element-211">
          <span className="inline-block bg-[#4ECDC4] text-[#1A2B4A] text-xs font-bold uppercase px-3 py-1 rounded-full mb-4 tracking-wider" data-id="element-212">
            The Numbers
          </span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-[#1A2B4A] mb-4 leading-tight" data-id="element-213">
            What Broken Recruiting Actually Costs You
          </h2>
          <p className="text-lg text-[#1A2B4A]/60" data-id="element-214">
            Cost per qualified hire — across every method.
          </p>
        </motion.div>

        {/* ─── DESKTOP: bar chart ─── */}
        <motion.div custom={1} variants={fadeUp} initial="hidden" animate={inView ? 'visible' : 'hidden'} className="hidden sm:block bg-white rounded-2xl shadow-md p-8 mb-6" data-id="element-215">
          <h3 className="text-base font-semibold text-[#1A2B4A]/50 uppercase tracking-wider mb-8" data-id="element-216">
            Cost Per Qualified Hire
          </h3>
          <div className="flex flex-col gap-5" data-id="element-217">
            {bars.map((bar, i) => <AnimatedBar key={bar.label} item={bar} index={i} animate={inView} data-id="element-218" />)}
          </div>
          <div className="mt-8 pt-6 border-t border-[#1A2B4A]/10" data-id="element-219">
            <div className="flex items-center gap-3 p-4 bg-[#4ECDC4]/10 rounded-xl" data-id="element-220">
              <div className="w-3 h-3 rounded-full bg-[#4ECDC4] flex-shrink-0" data-id="element-221" />
              <p className="text-sm font-semibold text-[#1A2B4A]" data-id="element-222">
                The Hire Advantage delivers qualified hires at{' '}
                <span className="text-[#4ECDC4]" data-id="element-223">90% less</span> than recruiting
                agencies.
              </p>
            </div>
          </div>
        </motion.div>

        {/* ─── MOBILE: real client stat cards ─── */}
        <div className="sm:hidden mb-6" data-id="element-224">
          <motion.div custom={1} variants={fadeUp} initial="hidden" animate={inView ? 'visible' : 'hidden'} className="bg-white rounded-2xl shadow-md p-6 mb-4" data-id="element-225">
            <p className="text-xs font-bold text-[#1A2B4A]/40 uppercase tracking-wider mb-4 text-center" data-id="element-226">
              Real Client Results
            </p>
            <div className="grid grid-cols-2 gap-3 mb-4" data-id="element-227">
              <MobileStatCard value={102} label="Final Interviews" sub="Booked through our system" accent={false} custom={1} animate={inView} data-id="element-228" />
              <MobileStatCard value={21} label="Hires Made" sub="Qualified, commission-ready reps" accent={true} custom={2} animate={inView} data-id="element-229" />
            </div>
            {/* Cost comparison — simplified for mobile */}
            <div className="space-y-2" data-id="element-230">
              <p className="text-xs font-bold text-[#1A2B4A]/40 uppercase tracking-wider mb-3" data-id="element-231">
                Cost Per Qualified Hire
              </p>
              {bars.map(bar => <div key={bar.label} className="flex items-center justify-between py-2 border-b border-[#1A2B4A]/6 last:border-0" data-id="element-232">
                  <span className={`text-sm ${bar.isAccent ? 'font-black text-[#1A2B4A]' : 'text-[#1A2B4A]/60'}`} data-id="element-233">
                    {bar.label}
                  </span>
                  <span className={`text-sm font-bold ${bar.isAccent ? 'text-[#4ECDC4]' : 'text-[#E8655A]'}`} data-id="element-234">
                    ${bar.value.toLocaleString()}
                  </span>
                </div>)}
            </div>
            <div className="mt-4 p-3 bg-[#4ECDC4]/10 rounded-xl" data-id="element-235">
              <p className="text-xs font-semibold text-[#1A2B4A] text-center" data-id="element-236">
                <span className="text-[#4ECDC4] font-black" data-id="element-237">90% less</span> than
                recruiting agencies
              </p>
            </div>
          </motion.div>
        </div>

        <motion.p custom={2} variants={fadeUp} initial="hidden" animate={inView ? 'visible' : 'hidden'} className="text-center text-sm text-[#1A2B4A]/40 mb-12" data-id="element-238">
          Based on average client data across 4,000+ hires.
        </motion.p>

        <motion.div custom={3} variants={fadeUp} initial="hidden" animate={inView ? 'visible' : 'hidden'} className="flex justify-center" data-id="element-239">
          <CTAButtonLight data-id="element-240" />
        </motion.div>
      </div>
    </section>;
}